import { filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpService } from 'services/http/http.service';

import { createQueryParam, buildQueryParams, createFileUploadHeaders } from 'utils/helpers';
import { QueryParamInterface, ListRequestParams } from 'utils/interfaces';
import { Observable, of } from 'rxjs';
import {
  Course,
  MappableCustFields,
  UploadedTemplateFile,
  RegistrationFormField,
  PublishCertificationData,
  CertificationTypes,
} from './certification.interface';
import { Certification } from 'interfaces/certification.interface';
import { requestCompleted, ShareRequest } from '@certemy/common';
import { ShowIfHttpError } from '@certemy/common';
import {
  ActivityType,
  DefaultActivityType,
} from 'components/organization-certification/step/education/general-ce-type/activity-type-control';
import { Step } from '../../objects/step.object';
import { ActionCreators } from 'features/certification/+state/certification.actions';
import { Selectors } from 'features/certification/+state/certification.state';
import { STEP_TYPES } from 'constants/status.constants';
import EDUCATION_STEP_CONSTANTS from 'constants/education-step.constants';
import { Certification as CertificationClass } from '../../objects/certification.object';
import { Store } from '@ngrx/store';
import _size from 'lodash-es/size';
import { WORKFLOW_MODULE_ID } from 'constants/workflow.constants';
import { AcronimCounter } from 'components/organization-certification/screens/info/info-screen.interface';
import { CertificationListQueryParam } from 'components/organization-certifications/certifications-list.interface';
import { SmartTable } from '@certemy/ui-components';

export enum CertificationResponseType {
  SelfEnrollment = 'self-enrollment',
  Assignment = 'assignment',
  Credential = 'credential',
}

export type CertificatesListRequestParams = ListRequestParams & { type?: CertificationResponseType };

@Injectable()
export class CertificationService {
  constructor(private http: HttpService, private store: Store<any>) {}
  workflowTypeId = WORKFLOW_MODULE_ID.CREDENTIAL;

  // TODO: Check where it can be used
  getCerts({
    search = null,
    order_id = 'name',
    order_type = 'asc',
    filters = null,
    page = 1,
    page_size = 1000,
    workflow_type_id = this.workflowTypeId,
    url = null,
    type = CertificationResponseType.Assignment,
  }: CertificatesListRequestParams) {
    const params = [
      createQueryParam('search', search),
      createQueryParam('order_id', order_id),
      createQueryParam('order_type', order_type),
      createQueryParam('filters', filters),
      createQueryParam('page', page),
      createQueryParam('page_size', page_size),
      createQueryParam('workflow_type_id', workflow_type_id),
      createQueryParam('type', type),
    ];
    const queryParams = buildQueryParams(params);
    url = url ? url : '/certification';

    return this.http.get(`${url}${queryParams}`);
  }

  @ShowIfHttpError('Cannot receive certification list')
  getCertificationList({
    limit = 10,
    offset = 0,
    orderId = 'name',
    orderType = SmartTable.Order.ASC,
    search = null,
    filters = null,
  }: CertificationListQueryParam) {
    const params = [
      createQueryParam('limit', limit),
      createQueryParam('offset', offset),
      createQueryParam('orderId', orderId),
      createQueryParam('orderType', orderType),
      createQueryParam('search', search),
      createQueryParam('filters', filters),
    ];

    const queryParams = buildQueryParams(params);

    return this.http.get(`/workflow/${this.workflowTypeId}${queryParams}`);
  }

  getUserAdditionalParameters() {
    return this.http.get(`/certification/additional_parameters`);
  }

  @ShowIfHttpError('Cannot receive additional parameters')
  getCertificationTypes(): Observable<CertificationTypes[]> {
    return this.http.get(`/workflow/${this.workflowTypeId}/sub-types`);
  }

  @ShowIfHttpError('An error occurred while getting the certificate. Please, try again')
  getCertification(certificationId: number): Observable<CertificationClass> {
    return this.http.get(`/workflow/${this.workflowTypeId}/${certificationId}`);
  }

  @ShowIfHttpError('An error occurred while creating the certificate. Please, try again')
  addCertification(cert: CertificationClass): Observable<{ workflowId: number }> {
    return this.http.post(`/workflow/${this.workflowTypeId}`, this.prepareCertForUpdateOrCreate(cert));
  }

  @ShowIfHttpError('An error occurred while publishing the certificate. Please, try again')
  publishCertification(certificationId, data?: PublishCertificationData) {
    return this.http.post(`/workflow/${this.workflowTypeId}/${certificationId}/publish`, data);
  }
  @ShowIfHttpError('An error occurred while updating certification.')
  updateCertification(certification): Observable<{ workflowId: number }> {
    const preparedCertification = this.prepareCertForUpdateOrCreate(certification);
    const certificationData = JSON.stringify(preparedCertification);
    const certificationId = certification.id;
    return this.http.put(`/workflow/${this.workflowTypeId}/${certificationId}`, certificationData);
  }

  @ShowIfHttpError('Could not delete credential.')
  deleteCertification(certificationIds: number[], workflowTypeId: number) {
    return this.http.post(`/workflow/${workflowTypeId}/delete`, { certificationIds });
  }

  @ShowIfHttpError('Could not delete credential.')
  verifyDeletionCertificates(certificationIds: number[], workflowTypeId: number) {
    return this.http.post(`/workflow/${workflowTypeId}/validate-deletion`, { certificationIds });
  }

  @ShowIfHttpError('Could not copy credential.')
  copyCertification({ certificationIds }: { certificationIds: number[] }) {
    return this.http.post(`/workflow/${this.workflowTypeId}/copy`, { certificationIds });
  }

  @ShowIfHttpError('An error occurred while updating certification.')
  updateAttribute(certifications: any[]) {
    return this.http.put(`/workflow/${WORKFLOW_MODULE_ID.CREDENTIAL}/update-attribute`, certifications);
  }

  getCourses(search, filters): Observable<Course[]> {
    const params = [
      createQueryParam('search', search),
      createQueryParam('filters', filters),
      createQueryParam('briefMode', true),
    ];
    const queryParams = buildQueryParams(params);
    return this.http.get(`/course?${queryParams}`).pipe(map(data => data.result));
  }

  prepareCertForUpdateOrCreate(cert: CertificationClass) {
    cert.validFromDate = cert.validFromDateModel;
    cert.validToDate = cert.validToDateModel;
    // BE accept only phase index
    cert.phases = cert.phases.map(phase => {
      let newInCompliancePhaseId;
      let newOutCompliancePhaseId;

      if (!phase.inCompliancePhaseId) {
        newInCompliancePhaseId = 0;
      } else {
        newInCompliancePhaseId = cert.phases.findIndex(ph => {
          return ph.phaseId === phase.inCompliancePhaseId;
        });

        newInCompliancePhaseId = newInCompliancePhaseId + 1;
      }

      if (!phase.outCompliancePhaseId) {
        newOutCompliancePhaseId = 0;
      } else {
        newOutCompliancePhaseId = cert.phases.findIndex(ph => {
          return ph.phaseId === phase.outCompliancePhaseId;
        });

        newOutCompliancePhaseId = newOutCompliancePhaseId + 1;
      }

      return {
        ...phase,
        inCompliancePhaseId: newInCompliancePhaseId,
        outCompliancePhaseId: newOutCompliancePhaseId,
      };
    });

    return cert;
  }

  getListOfSharedImposers({
    id,
    search = '',
    page = 1,
    pageSize = 10,
  }): Observable<Certification.GetSharedImposersResPayload> {
    const query = this.getQuery({ search, page, pageSize });

    // preview list
    return this.http.get(`/certification/${id}/shared/imposers${query}`);
  }

  getListOfAssignees(requestParams): Observable<Certification.ProfessionalsData> {
    const query = this.getQuery(requestParams);

    return this.http.get(`/certification/assignees${query}`).pipe(map(res => res.result));
  }

  getListOfAssigneesDashboard(requestParams): Observable<Certification.ProfessionalsData> {
    const query = this.getQuery(requestParams);

    return this.http.get(`/certification/assignees/dashboard${query}`).pipe(map(res => res.result));
  }

  @ShowIfHttpError('Can not get default registration form fields.')
  getDefaultRegistrationFields() {
    return this.http.get(`/certification/default_reg_form_fields`);
  }

  @ShowIfHttpError('Cannot receive registration form.')
  getCertificationRegForm(id): Observable<{ fields: RegistrationFormField[] }> {
    return this.http.get(`/certification/${id}/registration-form`);
  }

  getListOfImposers(id, search?, page?, pageSize?): Observable<Certification.GetImposersResPayload> {
    const query = this.getQuery({ search, page, pageSize });

    // edit list
    return this.http.get(`/certification/${id}/imposers${query}`);
  }

  updateCertificationShare(id, data) {
    return this.http.post(`/certification/${id}/shared`, data);
  }

  getCertificationInstanceDetails(id): Observable<Certification.ProfessionalDetails> {
    return this.http.get(`/certification_instance/${id}/assignee_details`).pipe(map(res => res.result));
  }

  getDataForTestRegistrationDashboardModal(requestParams) {
    const result: {
      professionalsData: Certification.ProfessionalsData;
      professionalDetails: Certification.ProfessionalDetails;
    } = {
      professionalsData: null,
      professionalDetails: null,
    };
    return this.getListOfAssigneesDashboard(requestParams).pipe(
      mergeMap((data: Certification.ProfessionalsData) => {
        result.professionalsData = data;
        const firstProfessional: Certification.Professional = data.professionals[0];

        if (firstProfessional) {
          return this.getCertificationInstanceDetails(firstProfessional.certification_instance_id);
        } else {
          return of(result.professionalDetails);
        }
      }),
      map((details: Certification.ProfessionalDetails) => {
        result.professionalDetails = details;
        return result;
      }),
    );
  }

  getListOfAssigneesForGeneralDashboard(params) {
    const query = this.getQuery(params);
    return this.http.get(`/dashboard/professional${query}`).pipe(
      map(res => {
        const { result, ...count } = res;
        return {
          professionals: result,
          ...count,
        };
      }),
    );
  }

  getListOfCertificatesForGeneralDashboard(requestParams) {
    const query = this.getQuery(requestParams);
    return this.http.get(`/dashboard/certification${query}`).pipe(
      map(res => {
        const { result, ...count } = res;
        return {
          certificates: result,
          ...count,
        };
      }),
    );
  }

  getDataForGeneralDashboardModal(requestParams) {
    const result: { professionalsData: Certification.ProfessionalsData; certificatesData: any } = {
      professionalsData: null,
      certificatesData: null,
    };
    return this.getListOfAssigneesForGeneralDashboard(requestParams).pipe(
      mergeMap((data: Certification.ProfessionalsData) => {
        result.professionalsData = data;
        const firstProfessional: Certification.Professional = data.professionals[0];

        if (firstProfessional) {
          const queryParams = {
            filters: {
              profile_id: [firstProfessional.profile_id],
              ...requestParams.filters,
            },
            page: 1,
          };
          return this.getListOfCertificatesForGeneralDashboard(queryParams);
        } else {
          return of(result.certificatesData);
        }
      }),
      map((certificates: any) => {
        result.certificatesData = certificates;
        return result;
      }),
    );
  }

  getCertificationVersions(workflowId: number): Observable<Certification.VersionsDetails> {
    return this.http.get(`/certification/${workflowId}/versions`);
  }

  private getQuery(params): string {
    const { search = '', page = 1, pageSize = 10, ...customParams } = params;
    const queryParams: QueryParamInterface[] = [
      createQueryParam('page', page),
      createQueryParam('page_size', pageSize),
    ];

    if (search) {
      queryParams.push(createQueryParam('search', search));
    }

    if (customParams) {
      for (const param in customParams) {
        if (customParams.hasOwnProperty(param)) {
          queryParams.push(createQueryParam(param, customParams[param]));
        }
      }
    }

    return buildQueryParams(queryParams);
  }

  uploadTemplateFile(file: File): Observable<UploadedTemplateFile> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(`/workflow/${WORKFLOW_MODULE_ID.CREDENTIAL}/file`, formData, {
      ...createFileUploadHeaders(),
      observe: 'events',
      params: { skipLoaderLock: true },
      reportProgress: true,
    });
  }

  @ShareRequest
  @ShowIfHttpError('Cannot receive Custom fields. Please try again later.')
  getMappableCustFields(): Observable<MappableCustFields> {
    return this.http.get(`/certification/custom_fields/list`).pipe(map(res => res.result));
  }

  @ShowIfHttpError('Cannot receive fields of the registration form. Please try again.')
  getRegistrationFormFields({ certificationId, profileId }) {
    return this.http.get(
      `/certification/${certificationId}/test-registration/registration-form/fields/profile/${profileId}`,
    );
  }

  @ShowIfHttpError('An error occurred while saving mappings. Please try again.')
  saveRegistrationFormMappings({ certificationId, profileId, changes }) {
    return this.http.post(
      `/certification/${certificationId}/test-registration/registration-form/fields/profile/${profileId}`,
      changes,
    );
  }

  @ShowIfHttpError('Cannot receive registration form fields mapping.')
  getRegistrationFormMapping({ certificationId, profileId }) {
    return this.http.get(
      `/certification/${certificationId}/test-registration/registration-form/mapping/profile/${profileId}`,
    );
  }

  @ShowIfHttpError('Cannot receive demographic survey fields mapping.')
  getDemographicSurveyMapping({ certificationId, profileId }) {
    return this.http.get(
      `/certification/${certificationId}/test-registration/demographic-survey/fields/profile/${profileId}`,
    );
  }

  @ShowIfHttpError('An error occurred while saving mappings. Please try again.')
  saveDemographicSurveyMapping({ certificationId, profileId, mapping }) {
    return this.http.post(
      `/certification/${certificationId}/test-registration/demographic-survey/fields/profile/${profileId}`,
      mapping,
    );
  }

  @ShowIfHttpError('Cannot receive registration form country states.')
  getRegistrationFormCountryStates(countryId, certificationId) {
    return this.http.get(`/certification/${certificationId}/registration-form/country/${countryId}/state`);
  }

  @ShowIfHttpError('Cannot receive default activity types.')
  getDefaultActivityTypes(): Observable<DefaultActivityType[]> {
    return this.http.get(`/certification/default-activity-types`);
  }

  validateAcronymCounter(data: AcronimCounter) {
    return this.http.post(`/organization-acronym-counter/validate`, data);
  }

  modifyEducationStep(certification: CertificationClass): Observable<any> {
    const educationStepWithAdvancedOptions = [];
    certification.phases.forEach(phase =>
      phase.steps.forEach(step => {
        if (this.isEducationGeneralCeStep(step)) {
          educationStepWithAdvancedOptions.push(step);
        }
      }),
    );

    if (_size(educationStepWithAdvancedOptions)) {
      return this.loadDefaultEducationActivities(educationStepWithAdvancedOptions);
    }

    return of(null);
  }

  private loadDefaultEducationActivities(steps: Step[]): Observable<any> {
    this.store.dispatch(new ActionCreators.GetDefaultEducationActivities());

    return requestCompleted(this.store.select(Selectors.getEducationDefaultActivitiesState)).pipe(
      filter(success => !!success),
      switchMap(() => this.store.select(Selectors.getEducationDefaultActivities)),
      tap(defaultActivities => steps.forEach(step => this.updateEducationActivitiesType(step, defaultActivities))),
    );
  }

  private updateEducationActivitiesType(step: Step, defaultActivities: DefaultActivityType[]) {
    this.updateActivityTypeIdIfDefault(step.content.activityTypes, defaultActivities);

    if (_size(step.content.requirements)) {
      step.content.requirements.forEach(requirement => {
        this.updateActivityTypeIdIfDefault(requirement.activityTypes, defaultActivities);

        if (_size(requirement.subrequirements)) {
          requirement.subrequirements.forEach(subrequirement =>
            this.updateActivityTypeIdIfDefault(subrequirement.activityTypes, defaultActivities),
          );
        }
      });
    }
  }

  private updateActivityTypeIdIfDefault(activityTypes: ActivityType[], defaultActivities: DefaultActivityType[]) {
    if (_size(activityTypes)) {
      activityTypes.forEach(activityType => {
        if (activityType.isDefault) {
          const defaultActivityType = defaultActivities.find(({ name }) => activityType.name === name) as ActivityType;
          activityType.id = defaultActivityType.id;
        }
      });
    }
  }

  private isEducationGeneralCeStep(step: Step) {
    return (
      step.step_type.id === STEP_TYPES.EDUCATION &&
      step.content.ce_type.id === EDUCATION_STEP_CONSTANTS.EDUCATION_TYPES.GENERAL_CE
    );
  }
}
